import { useMemo, useRef, useState } from 'react'

import type { Currency } from '@/bindings/Currency'
import type { Language } from '@/bindings/Language'
import type { MenuCategory } from '@/bindings/MenuCategory'
import type { OrderingItem } from '@/bindings/OrderingItem'
import type { QuantityStep } from '@/bindings/QuantityStep'
import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import { controlClass } from '@/components/control-class'
import { minorUnits } from '@/lib/currencies'
import { cx } from '@/lib/cx'
import { useLocalizedText } from '@/lib/i18n/localized-text'
import { useLanguage } from '@/lib/i18n/store'
import { useT } from '@/lib/i18n/translate'
import { STEP_LABELS, formatPrice, formatQuantity } from '@/lib/menu-format'
import { addStep, isMaxQuantity, parseQuantity, snapQuantity } from '@/lib/quantity'
import { useDismiss } from '@/lib/use-dismiss'

export interface MenuView {
  main: Language
  currency: Currency
  /** No quantity controls: the shop is closed, the menu stays readable. */
  readOnly: boolean
}

/**
 * The customer's menu (PRD, G6): categories in display order, items with the
 * branch's effective price; sold-out items show as such and cannot be added
 * (F4 — never the stock count). Tap a picture to see it large (F1/F3).
 */
export function MenuList({
  categories,
  items,
  view,
  quantityOf = () => '0',
  onQuantity = () => {},
}: {
  categories: readonly MenuCategory[]
  items: readonly OrderingItem[]
  view: MenuView
  /** Unused when `view.readOnly` (the branch page's reference menu, H1). */
  quantityOf?: (itemId: string) => string
  onQuantity?: (itemId: string, quantity: string) => void
}) {
  const t = useT()
  const language = useLanguage()
  const name = useLocalizedText(view.main)
  const [enlarged, setEnlarged] = useState<OrderingItem>()
  const decimals = minorUnits(view.currency)
  const grouped = useMemo(() => {
    const groups = new Map<string, OrderingItem[]>()
    for (const category of categories) groups.set(category.id, [])
    for (const item of items) groups.get(item.category_id)?.push(item)
    return groups
  }, [categories, items])
  const shown = categories.filter((category) => (grouped.get(category.id)?.length ?? 0) > 0)

  if (items.length === 0) return <p className="text-muted text-sm">{t('order.menuEmpty')}</p>
  return (
    <div className="flex flex-col gap-2">
      {shown.length > 1 && (
        <nav aria-label={t('order.categories')} className="bg-bg sticky top-14 z-20 -mx-4 overflow-x-auto px-4 py-2 sm:-mx-6 sm:px-6">
          <ul className="flex gap-2">
            {shown.map((category) => (
              <li key={category.id}>
                <button
                  type="button"
                  onClick={() => document.getElementById(`category-${category.id}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' })}
                  className="border-border bg-surface hover:bg-accent-soft min-h-9 rounded-full border px-3 text-sm whitespace-nowrap"
                >
                  {name(category.name)}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      )}
      {shown.map((category) => {
        const heading = name(category.name)
        return (
          <section key={category.id} id={`category-${category.id}`} aria-label={heading} className="scroll-mt-28">
            <h2 className="pt-3 text-lg font-semibold">{heading}</h2>
            <ul className="divide-border divide-y">
              {grouped.get(category.id)!.map((item) => {
                const label = name(item.name)
                return (
                  <li key={item.id} className="flex items-center gap-3 py-3">
                    {item.image_url ? (
                      <button
                        type="button"
                        aria-label={t('order.enlarge', { name: label })}
                        onClick={() => setEnlarged(item)}
                        className="focus-visible:ring-accent shrink-0 rounded-lg focus-visible:ring-2 focus-visible:outline-none"
                      >
                        <img src={item.image_url} alt="" className="size-20 rounded-lg object-cover" />
                      </button>
                    ) : (
                      <span aria-hidden className="bg-accent-soft size-20 shrink-0 rounded-lg" />
                    )}
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <p className="font-medium">{label}</p>
                      <p className="text-muted flex flex-wrap gap-x-3 text-sm">
                        <span>{formatPrice(item.price, decimals, language)}</span>
                        {item.step !== '1' && <span>{t(STEP_LABELS[item.step])}</span>}
                      </p>
                      {!item.orderable && (
                        <span>
                          <Badge tone="warning">{t('branchMenu.soldOut')}</Badge>
                        </span>
                      )}
                    </div>
                    {!view.readOnly && item.orderable && (
                      <QuantityControl
                        name={label}
                        step={item.step}
                        quantity={quantityOf(item.id)}
                        onChange={(quantity) => onQuantity(item.id, quantity)}
                      />
                    )}
                  </li>
                )
              })}
            </ul>
          </section>
        )
      })}
      {enlarged?.image_url && <PictureDialog url={enlarged.image_url} name={name(enlarged.name)} onClose={() => setEnlarged(undefined)} />}
    </div>
  )
}

/**
 * Add, or − quantity +: each press moves by the item's step as an exact decimal
 * string (F2/G1); the field between takes a typed amount for a big order. Text
 * exactly on the step lands in the cart as typed; anything else snaps to the
 * nearest step when the field is left, and a blank or 0 removes the line.
 */
export function QuantityControl({
  name,
  step,
  quantity,
  onChange,
  disabled = false,
}: {
  name: string
  step: QuantityStep
  quantity: string
  onChange: (quantity: string) => void
  disabled?: boolean
}) {
  const t = useT()
  const language = useLanguage()
  const [draft, setDraft] = useState<string>()
  const move = (direction: 1 | -1) => {
    setDraft(undefined)
    onChange(addStep(quantity, step, direction))
  }
  const type = (text: string) => {
    setDraft(text)
    const exact = parseQuantity(text, step)
    // Only a positive value lands live: a 0 mid-edit would swap the field away under the cursor.
    if (exact !== undefined && exact !== '0') onChange(exact)
  }
  const leave = () => {
    if (draft === undefined) return
    setDraft(undefined)
    const text = draft.trim() === '' ? '0' : draft
    onChange(parseQuantity(text, step) ?? snapQuantity(text, step) ?? quantity)
  }
  if (Number(quantity) <= 0) {
    return (
      <Button size="sm" variant="secondary" aria-label={t('order.addItem', { name })} disabled={disabled} onClick={() => move(1)}>
        {t('order.add')}
      </Button>
    )
  }
  return (
    <div className="flex shrink-0 items-center gap-1">
      <Button size="sm" variant="secondary" aria-label={t('order.decrease', { name })} disabled={disabled} onClick={() => move(-1)} className="min-w-9 px-0">
        −
      </Button>
      <input
        value={draft ?? formatQuantity(quantity, language, false)}
        inputMode="decimal"
        autoComplete="off"
        enterKeyHint="done"
        aria-label={t('order.quantityOf', { name })}
        disabled={disabled}
        onChange={(e) => type(e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={leave}
        // Enter commits the amount; in the cart it must not submit "Place order".
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            e.currentTarget.blur()
          }
        }}
        className={cx(controlClass, 'h-9 w-16 px-1 text-center font-medium tabular-nums')}
      />
      <Button
        size="sm"
        variant="secondary"
        aria-label={t('order.increase', { name })}
        disabled={disabled || isMaxQuantity(quantity, step)}
        onClick={() => move(1)}
        className="min-w-9 px-0"
      >
        +
      </Button>
    </div>
  )
}

/** The stored 512² picture (F1), full width; the backdrop, Escape, or the button closes it. */
function PictureDialog({ url, name, onClose }: { url: string; name: string; onClose: () => void }) {
  const t = useT()
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(true, ref, onClose)
  return (
    <div role="dialog" aria-modal="true" aria-label={name} className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div ref={ref} className="flex w-full max-w-lg flex-col items-center gap-3">
        <img src={url} alt={t('menu.pictureOf', { name })} className="aspect-square w-full rounded-xl object-cover" />
        <p className="text-lg font-medium text-white">{name}</p>
        <Button variant="secondary" onClick={onClose}>
          {t('order.close')}
        </Button>
      </div>
    </div>
  )
}
